import { fmtM } from '../lib/format';

export interface Slice {
  label: string;
  value: number;
  color: string;
}

/** The demo's donut: a 120×120 SVG ring with the total in the middle and a legend beside it. */
export function Donut({
  title,
  sub,
  slices,
  children,
}: {
  title: string;
  sub: string;
  slices: Slice[];
  children?: React.ReactNode;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (!total) {
    return (
      <article className="donut">
        <h3>{title}</h3>
        <div className="sub">{sub}</div>
        <p className="dempty">No streams in this selection.</p>
        {children}
      </article>
    );
  }
  const R = 52;
  const r = 32;
  const c = 60;
  const pt = (rad: number, ang: number) =>
    `${(c + rad * Math.cos(ang)).toFixed(2)},${(c + rad * Math.sin(ang)).toFixed(2)}`;
  let a0 = -Math.PI / 2;
  const paths = slices.map((s) => {
    const frac = s.value / total;
    const a1 = a0 + frac * Math.PI * 2;
    const big = frac > 0.5 ? 1 : 0;
    const d =
      frac >= 0.9999
        ? `M${pt(R, 0)} A${R},${R} 0 1 1 ${pt(R, Math.PI)} A${R},${R} 0 1 1 ${pt(R, 0)} Z M${pt(r, 0)} A${r},${r} 0 1 0 ${pt(r, Math.PI)} A${r},${r} 0 1 0 ${pt(r, 0)} Z`
        : `M${pt(R, a0)} A${R},${R} 0 ${big} 1 ${pt(R, a1)} L${pt(r, a1)} A${r},${r} 0 ${big} 0 ${pt(r, a0)} Z`;
    a0 = a1;
    return { ...s, d, frac };
  });
  return (
    <article className="donut">
      <h3>{title}</h3>
      <div className="sub">{sub}</div>
      <div className="dwrap">
        <svg viewBox="0 0 120 120" role="img" aria-label={title}>
          {paths.map((p) => (
            <path
              key={p.label}
              d={p.d}
              fill={p.color}
              stroke="var(--surface)"
              strokeWidth="2"
              fillRule="evenodd"
            >
              <title>
                {p.label}: {fmtM(p.value)}M ({(p.frac * 100).toFixed(1)}%)
              </title>
            </path>
          ))}
          <text
            x="60"
            y="58"
            textAnchor="middle"
            fontFamily="var(--mono)"
            fontSize="15"
            fontWeight="500"
            fill="var(--ink)"
          >
            {(total / 1e9).toFixed(2)}
          </text>
          <text
            x="60"
            y="71"
            textAnchor="middle"
            fontFamily="var(--mono)"
            fontSize="9"
            fill="var(--muted)"
          >
            bn streams
          </text>
        </svg>
        <ul className="dlegend">
          {paths.map((p) => (
            <li key={p.label}>
              <i style={{ background: p.color }} />
              <span className="l" title={p.label}>
                {p.label}
              </span>
              <span className="v">{(p.frac * 100).toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </div>
      {children}
    </article>
  );
}
